import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMapPin, FiPackage, FiTruck, FiSearch, FiX, FiCheckCircle } from 'react-icons/fi';
import { allCities } from '../data/cities';

const AutocompleteInput = ({ label, icon: Icon, value, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCities = async (query) => {
    try {
      setIsSearching(true);
      const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=5&language=id&format=json`);
      const data = await res.json();
      if (data && data.results) {
        const formatted = data.results.map(item => `${item.name}${item.admin1 ? ', ' + item.admin1 : ''}, ${item.country}`);
        // Remove duplicates
        setFilteredCities([...new Set(formatted)]);
        setIsOpen(true);
      } else {
        setFilteredCities([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (val.length > 2) {
      setIsSearching(true);
      setIsOpen(true);
      timeoutRef.current = setTimeout(() => {
        fetchCities(val);
      }, 500);
    } else {
      setFilteredCities([]);
      setIsSearching(false);
      setIsOpen(false);
    }
  };

  const handleSelect = (city) => {
    onChange(city);
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Icon className="text-gray-400" size={20} />
        </div>
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition-all ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
        />
      </div>
      
      {isOpen && !disabled && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 overflow-y-auto no-scrollbar">
          {isSearching ? (
            <li className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-brand-red border-t-transparent rounded-full animate-spin"></div> Mencari kota...
            </li>
          ) : filteredCities.length > 0 ? (
            filteredCities.map((city, index) => (
              <li 
                key={index}
                onClick={() => handleSelect(city)}
                className="px-4 py-3 hover:bg-brand-red hover:text-white cursor-pointer transition-colors text-sm flex items-center gap-2"
              >
                <FiSearch size={14} className="opacity-50 flex-shrink-0" /> <span className="truncate">{city}</span>
              </li>
            ))
          ) : value.length > 2 ? (
            <li className="px-4 py-3 text-sm text-gray-500">Kota tidak ditemukan</li>
          ) : null}
        </ul>
      )}
    </div>
  );
};

const Calculator = () => {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [weight, setWeight] = useState('');
  const [service, setService] = useState('Pengiriman Darat');
  
  // States for calculation result and modal
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // States for order modal form
  const [senderName, setSenderName] = useState('');
  const [senderWa, setSenderWa] = useState('');

  // Reset price if any input changes
  useEffect(() => {
    setCalculatedPrice(null);
  }, [origin, destination, weight, service]);

  const formatRupiah = (number) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(number);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius bumi dalam km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleCalculate = async () => {
    if (!origin || !destination || !weight) {
      alert('Mohon lengkapi kota asal, tujuan, dan berat barang.');
      return;
    }

    setIsCalculating(true);
    setCalculatedPrice(null);

    try {
      // Perbaikan: Open-Meteo hanya bisa mendeteksi nama domain utamanya, jadi kita split koma
      const originQuery = origin.split(',')[0].trim();
      const destQuery = destination.split(',')[0].trim();

      // 1. Call Geocoding API concurrently to get lat & long
      const [originRes, destRes] = await Promise.all([
        fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(originQuery)}&count=1&format=json`),
        fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(destQuery)}&count=1&format=json`)
      ]);
      
      const originData = await originRes.json();
      const destData = await destRes.json();
      
      let estPrice = 0;
      
      if (originData.results && destData.results) {
        const oLat = originData.results[0].latitude;
        const oLon = originData.results[0].longitude;
        const dLat = destData.results[0].latitude;
        const dLon = destData.results[0].longitude;
        
        const distanceKm = calculateDistance(oLat, oLon, dLat, dLon);
        let w = parseFloat(weight);

        let baseFee = 0;
        let ratePerKg = 0;
        const distanceModifier = distanceKm / 100; // per 100 km pengali harga agar stabil

        // Algoritma kargo yang jauh lebih rapi dan mirip dengan perhitungan logistik aslinya
        switch(service) {
          case "Pengiriman Darat": 
            baseFee = 50000; 
            ratePerKg = 4000 + (distanceModifier * 800); 
            break;
          case "Pengiriman Laut": 
            baseFee = 100000; 
            ratePerKg = 2500 + (distanceModifier * 400); 
            break;
          case "City Courier": 
            baseFee = 20000; 
            ratePerKg = 2000 + (distanceModifier * 5000); 
            break;
          case "Pengiriman Udara": 
            baseFee = 150000; 
            ratePerKg = 25000 + (distanceModifier * 1500); 
            break;
          case "Trucking": 
            baseFee = 500000; 
            ratePerKg = 1500 + (distanceModifier * 500); 
            break;
          case "Towing": 
            estPrice = 500000 + (distanceKm * 12000); 
            w = 0; // abaikan berat untuk mobil
            break;
          case "International Shipping": 
            baseFee = 500000; 
            ratePerKg = 40000 + (distanceModifier * 2000); 
            break;
          default: 
            baseFee = 50000; 
            ratePerKg = 4000 + (distanceModifier * 800); 
        }

        if (w > 0) estPrice = baseFee + (w * ratePerKg);

      } else {
        const basePrice = 50000;
        const weightRate = 12000;
        estPrice = basePrice + (parseFloat(weight) * weightRate);
      }

      if (estPrice < 50000) estPrice = 50000;
      estPrice = Math.ceil(estPrice / 1000) * 1000;

      await new Promise(r => setTimeout(r, 4000));
      
      setCalculatedPrice(estPrice);
    } catch (e) {
      console.error("Geocoding failed:", e);
      // Fallback in case of absolute network failure
      const fallbackPrice = 50000 + (parseFloat(weight) * 20000);
      await new Promise(r => setTimeout(r, 3000));
      setCalculatedPrice(Math.ceil(fallbackPrice / 1000) * 1000);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleWhatsAppOrder = (e) => {
    e.preventDefault();
    if (!senderName || !senderWa) {
      alert('Mohon lengkapi Nama dan Nomor WA Anda.');
      return;
    }

    const message = `Halo *Tanjung Priok Logistik*, saya ingin melakukan pemesanan pengiriman dengan detail berikut:

*Data Pengirim:*
Nama: ${senderName}
No. WA: ${senderWa}

*Detail Pengiriman:*
Asal: ${origin}
Tujuan: ${destination}
Berat: ${weight} kg
Layanan: ${service}
Estimasi Biaya: ${formatRupiah(calculatedPrice)}

Mohon info untuk proses selanjutnya. Terima kasih.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/6282315268197?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    setIsModalOpen(false);
  };

  return (
    <section id="calculator" className="py-20 bg-white relative z-20 -mt-10 md:-mt-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto bg-white rounded-3xl shadow-premium p-6 md:p-10 border border-gray-100"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Kalkulator <span className="text-brand-red">Pengiriman</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Gunakan fitur pencarian alamat otomatis kami. Dapatkan estimasi harga dan jadwalkan pengiriman Anda dengan mudah.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <AutocompleteInput 
              label="Kota Asal" 
              icon={FiMapPin} 
              value={origin} 
              onChange={setOrigin} 
              placeholder="Ketik kota asal..." 
            />
            
            <AutocompleteInput 
              label="Kota Tujuan" 
              icon={FiMapPin} 
              value={destination} 
              onChange={setDestination} 
              placeholder="Ketik kota tujuan..." 
            />

            <div className="w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Berat (Kg)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiPackage className="text-gray-400" size={20} />
                </div>
                <input
                  type="number"
                  min="1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Contoh: 50"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <div className="w-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Jenis Layanan</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiTruck className="text-gray-400" size={20} />
                </div>
                <select
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition-all appearance-none"
                >
                  <option value="Pengiriman Darat">Pengiriman Darat</option>
                  <option value="Pengiriman Laut">Pengiriman Laut</option>
                  <option value="City Courier">City Courier</option>
                  <option value="Pengiriman Udara">Pengiriman Udara</option>
                  <option value="Trucking">Trucking</option>
                  <option value="Towing">Towing</option>
                  <option value="International Shipping">International Shipping</option>
                </select>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {calculatedPrice && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8 overflow-hidden"
              >
                <div className="bg-brand-red/5 border border-brand-red/20 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center text-brand-red">
                      <FiCheckCircle size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Estimasi Biaya Pengiriman</p>
                      <h3 className="text-2xl md:text-3xl font-bold text-brand-black">{formatRupiah(calculatedPrice)}</h3>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 max-w-xs text-center md:text-right">
                    *Harga merupakan estimasi. Harga final akan dikonfirmasi oleh tim kami.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-center">
            {isCalculating ? (
              <button 
                disabled
                className="bg-brand-black text-white px-10 py-4 rounded-full font-bold text-lg cursor-not-allowed shadow-lg shadow-black/20 w-full md:w-auto flex items-center justify-center gap-3 relative overflow-hidden"
              >
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Menghitung Estimasi...
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent flex-shrink-0 w-[50%]"
                  animate={{ x: ['-200%', '300%'] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                />
              </button>
            ) : !calculatedPrice ? (
              <button 
                onClick={handleCalculate}
                className="bg-brand-black text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-brand-red transition-all duration-300 shadow-lg shadow-black/20 w-full md:w-auto"
              >
                Hitung Estimasi
              </button>
            ) : (
              <button 
                onClick={() => setIsModalOpen(true)}
                className="bg-brand-red text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-brand-darkRed transition-all duration-300 shadow-lg shadow-brand-red/30 w-full md:w-auto flex items-center justify-center gap-2"
              >
                Order Sekarang <FiTruck />
              </button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Order Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-brand-black/60 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            ></motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 w-full max-w-lg relative z-10"
            >
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-brand-red transition-colors"
              >
                <FiX size={24} />
              </button>

              <h3 className="text-2xl font-bold mb-2">Selesaikan Pesanan</h3>
              <p className="text-gray-600 mb-6 text-sm">Lengkapi data diri Anda, CS kami akan segera merespon.</p>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-6 text-sm">
                <div className="grid grid-cols-2 gap-y-2">
                  <span className="text-gray-500">Rute:</span>
                  <span className="font-medium text-right">{origin} - {destination}</span>
                  <span className="text-gray-500">Layanan:</span>
                  <span className="font-medium text-right">{service} ({weight}kg)</span>
                  <span className="text-gray-500">Estimasi:</span>
                  <span className="font-bold text-brand-red text-right">{formatRupiah(calculatedPrice)}</span>
                </div>
              </div>

              <form onSubmit={handleWhatsAppOrder}>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nama Pengirim</label>
                  <input
                    type="text"
                    required
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Masukkan nama lengkap"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nomor WhatsApp</label>
                  <input
                    type="tel"
                    required
                    value={senderWa}
                    onChange={(e) => setSenderWa(e.target.value)}
                    placeholder="Contoh: 08123456789"
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-red focus:border-transparent outline-none transition-all"
                  />
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#25D366]/20"
                >
                  Pesan Sekarang
                  <img src="/assets/images/whatsapp.svg" alt="WA" className="w-6 h-6 brightness-0 invert" />
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Calculator;